/**
 * 상단 바 (DESIGN_SPEC 6장 v1.2.1, 시안 03 README 5.2). 높이 56, 아래 hairline.
 *
 * - HomeTopBar: 별채대화 · 상회칠판. 가로 워드마크(찻잔 마크 26 + 「인생상회」 마루 부리 22) → 오른쪽 ☰ 대화 선택.
 *   04 에서 얼굴 줄과 화면 이름 라벨을 뺐습니다(탭 이름과 겹침). 화면 이름은 읽기 도구에만 남깁니다. 뒤로 없음(탭 화면)
 * - ChatTopBar: 1:1. 뒤로 + [아바타 32 + 호칭 / 소개 한 줄] 한 덩어리 버튼(→ 프로필) + 수첩 아이콘(04 D2e) + ☰
 * - TopBar: 그 밖의 화면. 뒤로 + 제목
 */
import { ChevronLeft, NotebookPen } from 'lucide-react';
import { getMember } from '@config/members/index.js';
import { Wordmark } from './Brand.jsx';
import { Avatar } from './Avatar.jsx';
import { SwitcherButton } from './ChatSwitcher.jsx';

const ICON_STROKE = 1.6;

export function IconButton({ label, onClick, children }) {
    return (
        <button
            type="button"
            aria-label={label}
            onClick={onClick}
            className="flex size-touch items-center justify-center text-text-primary"
        >
            {children}
        </button>
    );
}

export function TopBar({ title, onBack, trailing = null, bordered = true, children = null }) {
    return (
        <header className={`flex h-bar shrink-0 items-center gap-1 px-1 ${bordered ? 'border-b border-hairline' : ''}`}>
            {onBack ? (
                <IconButton label="뒤로" onClick={onBack}>
                    <ChevronLeft size={22} strokeWidth={ICON_STROKE} aria-hidden="true" />
                </IconButton>
            ) : null}
            {children ?? <h1 className="m-0 text-[17px] font-semibold">{title}</h1>}
            {trailing ? <div className="ml-auto">{trailing}</div> : null}
        </header>
    );
}

/**
 * @param {string} label  '별채대화' | '상회칠판' (읽기 도구용 화면 이름)
 */
export function HomeTopBar({ label }) {
    return (
        <header className="flex h-bar shrink-0 items-center border-b border-hairline pr-1 pl-4">
            <Wordmark />
            <h1 className="sr-only">{label}</h1>
            <div className="ml-auto">
                <SwitcherButton />
            </div>
        </header>
    );
}

export function ChatTopBar({ memberKey, onBack, onProfile, onMemo = null }) {
    const member = getMember(memberKey);
    return (
        <header className="flex h-bar shrink-0 items-center gap-1 border-b border-hairline px-1">
            <IconButton label="뒤로" onClick={onBack}>
                <ChevronLeft size={22} strokeWidth={ICON_STROKE} aria-hidden="true" />
            </IconButton>
            <button
                type="button"
                aria-label={`${member.name} 프로필`}
                onClick={onProfile}
                className="flex min-h-touch min-w-0 flex-1 items-center gap-2 rounded-card text-left"
            >
                <Avatar memberKey={memberKey} size={32} />
                <span className="flex min-w-0 flex-col">
                    <span className="text-[15px] leading-5 font-semibold">{member.name}</span>
                    <span className="truncate text-micro font-normal text-text-secondary">{member.line}</span>
                </span>
            </button>
            {onMemo ? (
                <IconButton label={`${member.name}이 아는 것`} onClick={onMemo}>
                    <NotebookPen size={22} strokeWidth={ICON_STROKE} aria-hidden="true" />
                </IconButton>
            ) : null}
            <SwitcherButton />
        </header>
    );
}
